import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone, HelpCircle } from 'lucide-react';

const WhatsAppChat = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // WhatsApp number - EDENLIFE International
  const whatsappNumber = '15149706528';

  const content = {
    fr: {
      title: 'Besoin d\'aide ?',
      subtitle: 'Contactez-nous sur WhatsApp',
      message: "Bonjour ! J'ai une question sur le Marathon de l'Avenir EDENLIFE 2026.",
      startChat: 'Démarrer le chat',
      call: 'Appeler',
      options: [
        { icon: HelpCircle, text: 'Question générale', msg: "Bonjour, j'ai une question générale sur le Marathon de l'Avenir EDENLIFE." },
        { icon: MessageCircle, text: 'Achat de billets', msg: "Bonjour, je voudrais acheter des billets pour le Marathon de l'Avenir EDENLIFE." },
        { icon: Phone, text: 'Support technique', msg: "Bonjour, j'ai besoin d'aide technique." }
      ]
    },
    en: {
      title: 'Need help?',
      subtitle: 'Contact us on WhatsApp',
      message: 'Hello! I have a question about the EDENLIFE Marathon 2026.',
      startChat: 'Start chat',
      call: 'Call',
      options: [
        { icon: HelpCircle, text: 'General question', msg: 'Hello, I have a general question about the EDENLIFE Marathon.' },
        { icon: MessageCircle, text: 'Ticket purchase', msg: 'Hello, I would like to buy tickets for the EDENLIFE Marathon.' },
        { icon: Phone, text: 'Technical support', msg: 'Hello, I need technical help.' }
      ]
    },
    ht: {
      title: 'Bezwen èd?',
      subtitle: 'Kontakte nou sou WhatsApp',
      message: 'Bonjou! Mwen gen yon kesyon sou Maraton EDENLIFE 2026.',
      startChat: 'Kòmanse chat',
      call: 'Rele',
      options: [
        { icon: HelpCircle, text: 'Kesyon jeneral', msg: 'Bonjou, mwen gen yon kesyon jeneral sou Maraton EDENLIFE.' },
        { icon: MessageCircle, text: 'Achte tikè', msg: 'Bonjou, mwen ta renmen achte tikè pou Maraton EDENLIFE.' },
        { icon: Phone, text: 'Sipò teknik', msg: 'Bonjou, mwen bezwen èd teknik.' }
      ]
    },
    es: {
      title: '¿Necesitas ayuda?',
      subtitle: 'Contáctanos en WhatsApp',
      message: '¡Hola! Tengo una pregunta sobre el Maratón EDENLIFE 2026.',
      startChat: 'Iniciar chat',
      call: 'Llamar',
      options: [
        { icon: HelpCircle, text: 'Pregunta general', msg: 'Hola, tengo una pregunta general sobre el Maratón EDENLIFE.' },
        { icon: MessageCircle, text: 'Compra de boletos', msg: 'Hola, me gustaría comprar boletos para el Maratón EDENLIFE.' },
        { icon: Phone, text: 'Soporte técnico', msg: 'Hola, necesito ayuda técnica.' }
      ]
    },
    pt: {
      title: 'Precisa de ajuda?',
      subtitle: 'Entre em contato pelo WhatsApp',
      message: 'Olá! Tenho uma pergunta sobre a Maratona EDENLIFE 2026.',
      startChat: 'Iniciar chat',
      call: 'Ligar',
      options: [
        { icon: HelpCircle, text: 'Pergunta geral', msg: 'Olá, tenho uma pergunta geral sobre a Maratona EDENLIFE.' },
        { icon: MessageCircle, text: 'Compra de bilhetes', msg: 'Olá, gostaria de comprar bilhetes para a Maratona EDENLIFE.' },
        { icon: Phone, text: 'Suporte técnico', msg: 'Olá, preciso de ajuda técnica.' }
      ]
    }
  };

  const t = content[i18n.language] || content.fr;

  const openWhatsApp = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
    setIsOpen(false);
  };

  return (
    <>
      {/* Chat Button - Repositioned for mobile */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-4 sm:bottom-24 sm:left-6 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        aria-label="WhatsApp Chat"
      >
        {isOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />}
      </motion.button>

      {/* Chat Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-20 sm:bottom-44 left-4 sm:left-6 z-50 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-green-500 p-4 text-white">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold">{t.title}</h3>
                  <p className="text-sm text-white/80">{t.subtitle}</p>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="p-4 space-y-3">
              {t.options.map((option, index) => {
                const Icon = option.icon;
                return (
                  <button
                    key={index}
                    onClick={() => openWhatsApp(option.msg)}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-100 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Icon className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="font-medium text-gray-700">{option.text}</span>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t p-4">
              <button
                onClick={() => openWhatsApp(t.message)}
                className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-colors"
              >
                <Send className="w-5 h-5" />
                <span>{t.startChat}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppChat;